class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.cart-drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.cart-drawer__inner') || this.querySelector('.cart-drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Remove empty state on the host element once we have cart contents
    if (this.classList.contains('is-empty')) {
      this.classList.remove('is-empty');
    }
    this.productId = parsedState.id;

    if (parsedState.sections) {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;

        const sectionHTML = parsedState.sections[section.id];
        if (!sectionHTML) return;

        const parsed = new DOMParser().parseFromString(sectionHTML, 'text/html');
        const targetEl = parsed.querySelector(section.selector || '.shopify-section');
        if (!targetEl) return;

        sectionElement.innerHTML = targetEl.innerHTML;
      });
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class BundleQuantity extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.addEventListener('click', this.onClick.bind(this));
  }
  async onClick(event) {
    const btn = event.target.closest('[data-bundle-qty-minus], [data-bundle-qty-plus]');
    if (!btn) return;
    event.preventDefault();
    if (this.dataset.loading === 'true') return;
    this.dataset.loading = 'true';
    this.querySelectorAll('button').forEach((b) => { b.disabled = true; });

    const qtyEl = this.querySelector('[data-bundle-qty]');
    const current = parseInt((qtyEl?.textContent || '1').trim(), 10) || 1;
    const delta = btn.hasAttribute('data-bundle-qty-plus') ? 1 : -1;
    const newQty = Math.max(0, current + delta);
    const keys = (this.dataset.bundleLineKeys || '').split('|').filter(Boolean);
    if (!keys.length) {
      this.dataset.loading = 'false';
      this.querySelectorAll('button').forEach((b) => { b.disabled = false; });
      return;
    }

    const updates = {};
    keys.forEach((k) => { updates[k] = newQty; });

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates,
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (state.sections) {
        if (state.sections['cart-drawer']) {
          const parsed = new DOMParser().parseFromString(state.sections['cart-drawer'], 'text/html');
          const newInner = parsed.querySelector('.cart-drawer__inner');
          const existingInner = document.querySelector('#CartDrawer .cart-drawer__inner');
          if (newInner && existingInner) {
            existingInner.innerHTML = newInner.innerHTML;
          }
        }
        if (state.sections['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(state.sections['cart-icon-bubble'], 'text/html');
          const newBubble = parsed.querySelector('.shopify-section');
          const existingBubble = document.getElementById('cart-icon-bubble');
          if (newBubble && existingBubble) {
            existingBubble.innerHTML = newBubble.innerHTML;
          }
        }
      }
    } catch (err) {
      console.error('Bundle quantity update failed', err);
    }
  }
}
customElements.define('bundle-quantity', BundleQuantity);
